import { Check } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useComponentDefaults } from "../../Provider";
import { registerOverlayClose } from "../../overlayStore";
import { clsx } from "../../utils/clsx";
import Button from "../Button/Button";
import Input from "../Input/Input";
import Popover from "../Popover/Popover";
import "./Select.css";

const getOptionValue = (option, fallback) => {
  if (option == null) return fallback;
  if (typeof option === 'object') {
    if (Object.prototype.hasOwnProperty.call(option, 'value')) return option.value;
    if (Object.prototype.hasOwnProperty.call(option, 'key')) return option.key;
    if (Object.prototype.hasOwnProperty.call(option, 'id')) return option.id;
  }
  return fallback;
};

const getOptionLabel = (option, fallback) => {
  if (option == null) return fallback;
  if (typeof option === 'object' && Object.prototype.hasOwnProperty.call(option, 'label')) {
    return option.label;
  }
  return fallback;
};

const firstEnabledIndex = (items) => items.findIndex((item) => !item.disabled);

const findNextEnabled = (items, start, step) => {
  if (!items.length) return -1;
  let idx = start;
  for (let i = 0; i < items.length; i += 1) {
    idx = (idx + step + items.length) % items.length;
    if (!items[idx].disabled) return idx;
  }
  return start;
};

export default function Select(props) {
  const intl = useIntl();

  const merged = useComponentDefaults(
    'Select',
    props,
    {
      placeholder: 'Velg …',
      disabled: false,
      className: '',
      variant: 'default',
      shape: 'default',
      color: 'default',
      searchable: true,
      emptyMessage: '',
      allowClear: true,
      searchPlaceholder: '',
      multiple: false,
    },
    { demoteThemeKeys: ['color'] }
  );

  const {
    options = [],
    value,
    defaultValue = '',
    onChange,
    placeholder = 'Velg …',
    disabled = false,
    id,
    name,
    className = '',
    style,
    ariaLabel,
    variant = 'default',
    shape = 'default',
    color = 'default',
    searchable = false,
    emptyMessage = '',
    filterOption,
    allowClear = false,
    clearAriaLabel,
    searchPlaceholder,
    popupZIndex,
    multiple = false,
    mode,
  } = merged;
  const isMultiple = Boolean(multiple || mode === 'multiple');

  const fallbackEmptyMessage = intl.formatMessage({ id: 'designSystem.select.noResults', defaultMessage: 'Not found' });
  const fallbackClearLabel = intl.formatMessage({ id: 'designSystem.select.clear', defaultMessage: 'Clear selection' });
  const fallbackSearchPlaceholder = intl.formatMessage({ id: 'designSystem.select.searchPlaceholder', defaultMessage: 'Type to search' });

  const resolvedEmptyMessage = emptyMessage || fallbackEmptyMessage;
  const resolvedClearAriaLabel = clearAriaLabel || fallbackClearLabel;
  const resolvedSearchPlaceholder = searchPlaceholder || fallbackSearchPlaceholder;

  const generatedId = React.useId();
  const selectId = id || generatedId;
  const menuId = `${selectId}-menu`;

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt, index) => {
      const fallbackValue = opt ?? index;
      const rawValue = getOptionValue(opt, fallbackValue);
      const valueResolved = rawValue === undefined || rawValue === null ? fallbackValue : rawValue;
      const labelResolved = getOptionLabel(opt, String(valueResolved ?? index));
      const disabledResolved = Boolean(opt && typeof opt === 'object' && 'disabled' in opt ? opt.disabled : false);
      const original = typeof opt === 'object' && opt !== null
        ? opt
        : { value: valueResolved, label: labelResolved };
      return {
        key: `${index}-${String(valueResolved)}`,
        value: valueResolved,
        label: labelResolved,
        disabled: disabledResolved,
        original,
      };
    });
  }, [options]);

  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleOptions = useMemo(() => {
    if (!searchable) return normalizedOptions;
    if (!normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((option) => {
      if (typeof filterOption === 'function') {
        return filterOption(searchQuery, option.original ?? option, option);
      }
      const label = option.label ? String(option.label).toLowerCase() : '';
      const valueText = option.value != null ? String(option.value).toLowerCase() : '';
      return label.includes(normalizedQuery) || valueText.includes(normalizedQuery);
    });
  }, [filterOption, normalizedOptions, normalizedQuery, searchable, searchQuery]);

  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue ?? (isMultiple ? [] : ''));

  useEffect(() => {
    if (!isControlled) setInternal(defaultValue ?? (isMultiple ? [] : ''));
  }, [defaultValue, isControlled, isMultiple]);

  const selectedValue = useMemo(
    () => (isControlled ? value ?? (isMultiple ? [] : '') : internal ?? (isMultiple ? [] : '')),
    [internal, isControlled, isMultiple, value]
  );
  const selectedValues = useMemo(() => {
    if (isMultiple) {
      if (Array.isArray(selectedValue)) return selectedValue.filter((item) => item !== null && item !== undefined);
      if (selectedValue === null || selectedValue === undefined || selectedValue === '') return [];
      return [selectedValue];
    }
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') return [];
    return [selectedValue];
  }, [isMultiple, selectedValue]);
  const selectedStrings = useMemo(() => selectedValues.map((item) => String(item)), [selectedValues]);
  const selectedSet = useMemo(() => new Set(selectedStrings), [selectedStrings]);
  const selectedString = isMultiple ? (selectedStrings[0] || '') : (selectedStrings[0] || '');
  const selectedIndex = useMemo(() => {
    if (!selectedStrings.length) return -1;
    return normalizedOptions.findIndex((opt) => selectedSet.has(String(opt.value)));
  }, [normalizedOptions, selectedSet, selectedStrings.length]);
  const selectedVisibleIndex = useMemo(() => {
    if (!selectedStrings.length) return -1;
    return visibleOptions.findIndex((opt) => selectedSet.has(String(opt.value)));
  }, [selectedSet, selectedStrings.length, visibleOptions]);
  const hasValue = selectedStrings.length > 0;

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const typeaheadRef = useRef('');
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(0);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (event) => {
      const container = containerRef.current;
      const popoverContent = menuRef.current;
      if (!container && !popoverContent) return;
      if (container?.contains(event.target)) return;
      if (popoverContent?.contains(event.target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', onDocumentClick);
    const unregister = registerOverlayClose(() => closeMenu());
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      unregister?.();
    };
  }, [open, closeMenu]);

  useEffect(() => {
    if (!open) {
      typeaheadRef.current = '';
      if (searchQuery) setSearchQuery('');
      setHighlight(-1);
    }
  }, [open, searchQuery]);

  useEffect(() => {
    if (!open) return;
    if (!visibleOptions.length) {
      setHighlight(-1);
      return;
    }

    if (searchable && searchQuery) {
      const firstEnabled = firstEnabledIndex(visibleOptions);
      if (
        highlight < 0
        || highlight >= visibleOptions.length
        || visibleOptions[highlight]?.disabled
      ) {
        if (firstEnabled !== -1) setHighlight(firstEnabled);
      }
      return;
    }

    if (selectedVisibleIndex >= 0) {
      setHighlight(selectedVisibleIndex);
      return;
    }

    const firstEnabled = firstEnabledIndex(visibleOptions);
    if (highlight < 0 || highlight >= visibleOptions.length || visibleOptions[highlight]?.disabled) {
      setHighlight(firstEnabled);
    }
  }, [open, visibleOptions, selectedVisibleIndex, highlight, searchable, searchQuery]);

  useEffect(() => {
    if (searchable) return;
    typeaheadRef.current = '';
    if (searchQuery) setSearchQuery('');
  }, [searchable, searchQuery]);

  const moveHighlight = useCallback(
    (direction) => {
      const start = highlight >= 0 ? highlight : selectedVisibleIndex;
      const base = start >= 0 ? start : firstEnabledIndex(visibleOptions);
      const nextIndex = findNextEnabled(visibleOptions, base < 0 ? 0 : base, direction);
      setHighlight(nextIndex);
    },
    [highlight, selectedVisibleIndex, visibleOptions]
  );

  const resetTypeahead = useCallback(() => {
    typeaheadRef.current = '';
    if (searchQuery) setSearchQuery('');
  }, [searchQuery]);

  const commitTypeahead = useCallback(
    (nextValue) => {
      typeaheadRef.current = nextValue;
      setSearchQuery(nextValue);
    },
    []
  );

  const handleTypeAheadKey = useCallback(
    (event) => {
      if (!searchable || disabled) return false;
      if (event.altKey || event.ctrlKey || event.metaKey) return false;

      const { key } = event;

      if (key.length === 1 && /\S/.test(key)) {
        const nextValue = `${typeaheadRef.current}${key}`;
        commitTypeahead(nextValue);
        if (!open) setOpen(true);
        event.preventDefault();
        return true;
      }

      if (key === 'Backspace' || key === 'Delete') {
        if (typeaheadRef.current) {
          const nextValue = typeaheadRef.current.slice(0, -1);
          typeaheadRef.current = nextValue;
          setSearchQuery(nextValue);
          event.preventDefault();
          return true;
        }
        return false;
      }

      if (key === 'Escape' && typeaheadRef.current) {
        resetTypeahead();
        event.preventDefault();
        return true;
      }

      return false;
    },
    [searchable, disabled, open, commitTypeahead, resetTypeahead]
  );

  const updateMenuWidth = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const nextWidth = container.offsetWidth || 0;
    setMenuWidth((prev) => (prev === nextWidth ? prev : nextWidth));
  }, []);

  useEffect(() => {
    updateMenuWidth();
  }, [updateMenuWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => updateMenuWidth();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateMenuWidth]);

  useEffect(() => {
    if (!open || !searchable) return;
    const input = searchInputRef.current;
    if (!input) return;

    const focusTimer = window.requestAnimationFrame(() => {
      input.focus({preventScroll: true});
      const valueLength = input.value.length;
      input.setSelectionRange(valueLength, valueLength);
    });

    return () => {
      window.cancelAnimationFrame(focusTimer);
    };
  }, [open, searchable]);

  const selectOption = useCallback(
    (option) => {
      if (!option || option.disabled) return;
      if (isMultiple) {
        const optionString = String(option.value);
        const exists = selectedSet.has(optionString);
        const nextValues = exists
          ? selectedValues.filter((item) => String(item) !== optionString)
          : [...selectedValues, option.value];
        if (!isControlled) setInternal(nextValues);
        onChange?.(nextValues, option.original);
        return;
      }
      if (!isControlled) setInternal(option.value);
      onChange?.(option.value, option.original);
      resetTypeahead();
      setOpen(false);
    },
    [isControlled, isMultiple, onChange, resetTypeahead, selectedSet, selectedValues]
  );

  const clearSelection = useCallback(() => {
    if (!allowClear || !hasValue) return;
    if (!isControlled) setInternal(isMultiple ? [] : '');
    onChange?.(isMultiple ? [] : null, null);
    setOpen(false);
    setHighlight(-1);
    resetTypeahead();
  }, [allowClear, hasValue, isControlled, isMultiple, onChange, resetTypeahead]);

  const handleTriggerKeyDown = (event) => {
    if (handleTypeAheadKey(event)) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open && !disabled && canDisplayMenu) setOpen(true);
        moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open && !disabled && canDisplayMenu) setOpen(true);
        moveHighlight(-1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (open && highlight >= 0 && visibleOptions[highlight]) {
          selectOption(visibleOptions[highlight]);
        } else if (!open && !disabled && canDisplayMenu) {
          setOpen(true);
        }
        break;
      }
      case 'Backspace':
      case 'Delete':
        if (allowClear && hasValue) {
          event.preventDefault();
          clearSelection();
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      default:
        break;
    }
  };

  const menuActiveId = highlight >= 0 && highlight < visibleOptions.length
    ? `${menuId}-opt-${highlight}`
    : undefined;

  const isShowingSearchQuery = searchable && Boolean(searchQuery);
  const shouldShowSearchPlaceholder = searchable && open && !searchQuery && !hasValue;

  const displayLabel = (() => {
    if (isShowingSearchQuery) return searchQuery;
    if (shouldShowSearchPlaceholder) return resolvedSearchPlaceholder || placeholder;
    if (isMultiple && hasValue) {
      const selectedLabels = normalizedOptions
        .filter((opt) => selectedSet.has(String(opt.value)))
        .map((opt) => opt.label);
      return selectedLabels.join(', ');
    }
    if (hasValue && normalizedOptions[selectedIndex]) {
      return normalizedOptions[selectedIndex].label;
    }
    return placeholder;
  })();

  const labelHasPlaceholderStyle = !isShowingSearchQuery && (!hasValue || shouldShowSearchPlaceholder);

  const hiddenValue = isMultiple ? selectedValues.join(',') : selectedString;

  const firstEnabledVisible = firstEnabledIndex(visibleOptions);
  const canDisplayMenu = searchable || (visibleOptions.length > 0 && firstEnabledVisible !== -1);
  const menuVisible = open && canDisplayMenu;

  useEffect(() => {
    if (menuVisible) {
      updateMenuWidth();
    }
  }, [menuVisible, updateMenuWidth]);

  useEffect(() => {
    if (open && !canDisplayMenu) {
      setOpen(false);
    }
  }, [canDisplayMenu, open]);

  const toggleMenu = useCallback(() => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev) return false;
      if (!canDisplayMenu) return prev;
      return true;
    });
  }, [canDisplayMenu, disabled]);

  const handlePopoverOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen) {
        closeMenu();
        return;
      }
      if (disabled || !canDisplayMenu) return;
      setOpen(true);
    },
    [canDisplayMenu, closeMenu, disabled],
  );

  const canClear = allowClear && hasValue && !disabled;
  const clearLabelText = resolvedClearAriaLabel;

  const handleClearMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClearClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    clearSelection();
  };

  const handleSearchInputChange = useCallback(
    (event) => {
      const { value: nextValue } = event.target;
      typeaheadRef.current = nextValue;
      setSearchQuery(nextValue);
      if (!open) setOpen(true);
    },
    [open]
  );

  const handleSearchInputKeyDown = useCallback(
    (event) => {
      if (!open) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveHighlight(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveHighlight(-1);
          break;
        case 'Enter':
          if (highlight >= 0 && visibleOptions[highlight]) {
            event.preventDefault();
            selectOption(visibleOptions[highlight]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          closeMenu();
          break;
        default:
          break;
      }
    },
    [closeMenu, highlight, moveHighlight, open, selectOption, visibleOptions]
  );

  const resolvedMenuWidth = menuWidth || containerRef.current?.offsetWidth || 0;

  const popoverContent = menuVisible ? (
    <div
      ref={menuRef}
      className="ds-select__popover"
      style={resolvedMenuWidth ? { '--ds-select-popover-width': `${resolvedMenuWidth}px` } : undefined}
    >
      {searchable ? (
        <div className="ds-select__search" role="presentation">
          <Input
            ref={searchInputRef}
            size="small"
            value={searchQuery}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchInputKeyDown}
            placeholder={resolvedSearchPlaceholder}
            aria-label={resolvedSearchPlaceholder || placeholder}
          />
        </div>
      ) : null}
      <div
        id={menuId}
        role="listbox"
        aria-activedescendant={menuActiveId}
        tabIndex={-1}
        className="ds-select__options"
      >
        {visibleOptions.length
          ? visibleOptions.map((option, index) => {
              const isSelected = String(option.value) === selectedString;
              const isMultiSelected = selectedSet.has(String(option.value));
              const optionId = `${menuId}-opt-${index}`;
              return (
                <Button
                  key={option.key}
                  id={optionId}
                  role="option"
                  size="small"
                  aria-selected={isMultiple ? isMultiSelected : isSelected}
                  type={(isMultiple ? isMultiSelected : isSelected) ? 'primary' : 'text'}
                  className="relationPopoverButton"
                  disabled={option.disabled}
                  onMouseEnter={() => !option.disabled && setHighlight(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                  icon={(isMultiple ? isMultiSelected : isSelected) ? <Check size={16} /> : null}
                >
                {option.label}
                </Button>
              );
            })
          : resolvedEmptyMessage ? (
              <div className="ds-select__empty" role="status">
                {resolvedEmptyMessage}
              </div>
            ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={clsx(
        'ds-select',
        `ds-select--variant-${variant}`,
        `ds-select--shape-${shape}`,
        color !== 'default' && `ds-select--color-${color}`,
        hasValue && 'ds-select--has-value',
        disabled && 'ds-select--disabled',
        className,
      )}
      style={style}
    >
      {name ? <input type="hidden" name={name} value={hiddenValue} /> : null}
      <Popover
        trigger={[]}
        arrow={false}
        placement="bottomLeft"
        open={menuVisible}
        onOpenChange={handlePopoverOpenChange}
        closeOnBlur={false}
        destroyOnHidden
        className="ds-select__popover-host"
        content={popoverContent}
        zIndex={popupZIndex}
      >
        <button
          type="button"
          id={selectId}
          className={clsx(
            'ds-input',
            'ds-select__trigger',
            `ds-input--variant-${variant}`,
            `ds-input--shape-${shape}`,
            color !== 'default' && `ds-input--color-${color}`,
          )}
          role="combobox"
          aria-label={ariaLabel || placeholder}
          aria-expanded={menuVisible}
          aria-haspopup="listbox"
          aria-controls={menuVisible ? menuId : undefined}
          aria-disabled={disabled}
          onClick={toggleMenu}
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
        >
          <span className={clsx('ds-select__label', labelHasPlaceholderStyle && 'ds-select__label--placeholder')}>
            {displayLabel}
          </span>
          {canClear ? (
            <span
              className="ds-select__clear"
              title={clearLabelText}
              aria-hidden="true"
              onMouseDown={handleClearMouseDown}
              onTouchStart={handleClearMouseDown}
              onClick={handleClearClick}
              onTouchEnd={handleClearClick}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          ) : (
            <span className="ds-select__arrow" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          )}
        </button>
      </Popover>
    </div>
  );
}
